import $ from 'jquery';

/**
 * Tabs, per the ARIA authoring practices.
 *
 * `.active` used to drive everything, which left the set announced as a row of
 * generic buttons: no way to tell which tab was current, or that it was one of
 * four. A tab is also `aria-selected`, and a tablist takes ONE tab stop with
 * the arrows moving between tabs (a roving tabindex) rather than every tab
 * being its own stop.
 *
 * Both tab blocks land on the same `.col-tabs` / `.col-tab-content` shape, so
 * they share the behaviour — the Gutenberg one just has to be assembled first.
 */

/**
 * Point a set at one tab: selection, the roving tab stop, and the panel.
 *
 * @param {jQuery}  $tabs    Every tab in the tablist.
 * @param {jQuery}  $panels  Every panel.
 * @param {jQuery}  $tab     The tab to select.
 * @param {boolean} setFocus Move focus too (keyboard), or leave it (click).
 */
function selectTab($tabs, $panels, $tab, setFocus) {
	if (!$tab || !$tab.length) {
		return;
	}

	$tabs.removeClass('active').attr({ 'aria-selected': 'false', tabindex: '-1' });
	$tab.addClass('active').attr({ 'aria-selected': 'true', tabindex: '0' });

	const target = $tab.attr('attr-target');
	$panels.removeClass('active');
	$panels.filter(`#content-${target}`).addClass('active');

	if (setFocus) {
		$tab.trigger('focus');
	}
}

/**
 * Home / End / arrow handling. Which arrows apply follows the tablist's
 * orientation, so a vertical set doesn't answer Left/Right and vice versa.
 *
 * @param {jQuery} $tabs       Every tab in the tablist.
 * @param {jQuery} $panels     Every panel.
 * @param {Event}  e           Keydown event.
 * @param {string} orientation 'horizontal' | 'vertical'.
 * @return {boolean} Whether the key was handled.
 */
function handleArrowKeys($tabs, $panels, e, orientation) {
	const prevKey = orientation === 'vertical' ? 'ArrowUp' : 'ArrowLeft';
	const nextKey = orientation === 'vertical' ? 'ArrowDown' : 'ArrowRight';
	const index = $tabs.index(e.currentTarget);
	let target = -1;

	if (e.key === prevKey) {
		target = index <= 0 ? $tabs.length - 1 : index - 1;
	} else if (e.key === nextKey) {
		target = index >= $tabs.length - 1 ? 0 : index + 1;
	} else if (e.key === 'Home') {
		target = 0;
	} else if (e.key === 'End') {
		target = $tabs.length - 1;
	} else {
		return false;
	}

	e.preventDefault();
	selectTab($tabs, $panels, $tabs.eq(target), true);
	return true;
}

/**
 * Wire one assembled tablist.
 *
 * @param {jQuery} $tabsCol    The tablist.
 * @param {jQuery} $contentCol The panel container.
 */
function bindTablist($tabsCol, $contentCol) {
	if (!$tabsCol.length || !$contentCol.length) {
		return;
	}
	const $tabs = $tabsCol.find('.tab');
	const $panels = $contentCol.find('.content');
	if (!$tabs.length) {
		return;
	}
	const orientation = $tabsCol.attr('aria-orientation') === 'vertical' ? 'vertical' : 'horizontal';

	$tabs.off('click.ggTabs keydown.ggTabs');

	$tabs.on('click.ggTabs', function (e) {
		e.preventDefault();
		// A click already moves focus — don't fight it.
		selectTab($tabs, $panels, $(this), false);
	});

	$tabs.on('keydown.ggTabs', function (e) {
		if (handleArrowKeys($tabs, $panels, e, orientation)) {
			return;
		}
		if (e.key === 'Enter' || e.key === ' ') {
			e.preventDefault();
			selectTab($tabs, $panels, $(this), false);
		}
	});

	// Open whatever is already marked active, else the first — either way this
	// settles the state so exactly one tab owns the tab stop.
	const $initial = $tabs.filter('.active').first();
	selectTab($tabs, $panels, $initial.length ? $initial : $tabs.first(), false);
}

export default function initTabModules() {
	// Legacy ACF block: tabs and panels are already in place.
	$('.container-tab-module:not(.wp-block-taoti-tab-block-g)').each(function () {
		const $block = $(this);
		bindTablist($block.find('.col-tabs').first(), $block.find('.col-tab-content').first());
	});

	// Gutenberg block: each panel ships its own tab, so lift them into the
	// tablist before binding — the roles only line up once the DOM does.
	$('.wp-block-taoti-tab-block-g').each(function () {
		const $block = $(this);
		const $tabsCol = $block.find('.col-tabs').first();
		const $contentCol = $block.find('.col-tab-content').first();
		if (!$tabsCol.length || !$contentCol.length) {
			return;
		}

		$contentCol.children('.wp-block-taoti-tab-panel-g, .tab-panel-g').each(function () {
			const $panel = $(this);
			const $tabBtn = $panel.children('.tab').first();
			const $content = $panel.children('.content').first();
			if ($tabBtn.length && $content.length) {
				$tabsCol.append($tabBtn);
				$content.insertBefore($panel);
				$panel.remove();
			}
		});

		bindTablist($tabsCol, $contentCol);
	});
}
